import {computed, makeObservable} from 'mobx';

import type {ID, Nullable} from '../../../utils/utility-types';
import type {CardModel} from '../Card/Card.model';
import type {CourseModel} from '../Course/Course.model';
import type {CurrencyModel} from '../Currency/Currency.model';
import type {DateTimeModel} from '../Date/DateTime.model';
import type {OfferModel} from '../Offer/Offer.model';
import type {PriceModel} from '../Price/Price.model';
import type {UserModel} from '../User/User.model';

import {BaseModel} from '../../../core/domain/Base/Base.model';
import {RefundModel} from '../Refund/Refund.model';

const ModelName = 'PaymentModel';

export class PaymentModel extends BaseModel {
  amount: PriceModel;
  card: Nullable<CardModel>;
  cardId: Nullable<string>;
  coaches: UserModel[];
  course: Nullable<CourseModel>;
  createdDate: DateTimeModel;
  currency: CurrencyModel;
  endDate: DateTimeModel;
  offer: Nullable<OfferModel>;
  originalPaymentIds: string[];
  paidDate: Nullable<DateTimeModel>;
  processing: PaymentModel.Processing;
  refundedAmount: PriceModel;
  refunds: RefundModel[];
  startDate: DateTimeModel;
  status: PaymentModel.Status;
  transfers: Record<ID, PaymentModel.Transfer>;
  type: PaymentModel.Type;
  updatedDate: DateTimeModel;
  user: UserModel;
  vendorPaymentId: Nullable<string>;

  constructor(props: PaymentModel.Props) {
    super(props);

    Object.assign(this, props);

    makeObservable(
      this,
      BaseModel.mobxAnnotationFactory<PaymentModel, PaymentModel.Props>(props, {
        hasTransfers: computed,
        isFailed: computed,
        isFullyRefunded: computed,
        isRefunded: computed,
        isSucceeded: computed,
      }),
    );
  }

  get hasTransfers() {
    return Object.keys(this.transfers).length > 0;
  }

  get isFailed() {
    return PaymentModel.FAILED_STATUSES.includes(this.status);
  }

  get isFullyRefunded() {
    return (
      this.isRefunded &&
      this.refundedAmount.value >=
        this.amount.value - RefundModel.minRefundAmount
    );
  }

  get isRefunded() {
    return this.refundedAmount.value > 0;
  }

  get isSucceeded() {
    return PaymentModel.SUCCEEDED_STATUSES.includes(this.status);
  }

  static override get modelName(): typeof ModelName {
    return ModelName;
  }

  get modelName(): typeof ModelName {
    return ModelName;
  }
}

export namespace PaymentModel {
  export enum Processing {
    EXTERNAL = 'external',
    INTERNAL = 'internal',
  }

  export enum Status {
    COMPLETE = 'complete',
    FAILED = 'failed',
    FROZEN = 'frozen',
    PAID = 'paid',
    PENDING = 'pending',
    PROCESSING = 'processing',
    REQUIRES_ACTION = 'requires_action',
    SUSPENDED = 'suspended',
  }

  export enum Type {
    OFFER = 'offer',
    PROGRAM = 'program',
    REFUND = 'refund',
    SUBSCRIPTION = 'subscription',
  }

  export type Props = BaseModel.ModelToProps<
    PaymentModel,
    | 'hasTransfers'
    | 'isFailed'
    | 'isFullyRefunded'
    | 'isRefunded'
    | 'isSucceeded'
  >;

  export type Transfer = {
    amount: PriceModel;
    coach: Nullable<UserModel>;
    id: Nullable<string>;
    portion: number;
    reversedAmount: PriceModel;
    transferredDate: Nullable<DateTimeModel>;
  };

  /**
   * Failed status means that attempt of charge was failed
   * but the backend will try to perform it again
   *
   * Frozen is a permanently failed status
   */
  export const FAILED_STATUSES = [
    PaymentModel.Status.FAILED,
    PaymentModel.Status.FROZEN,
  ];

  /**
   * Paid status means that payment was successfully charged via vendor
   *
   * Complete status means that payment was successfully transferred via vendor
   */
  export const SUCCEEDED_STATUSES = [
    PaymentModel.Status.COMPLETE,
    PaymentModel.Status.PAID,
  ];
}
